import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { ParkingaboLayoutWithHeader } from '../../components/layout/ParkingaboLayoutWithHeader.tsx';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useParamOrNull } from 'dg-web-shared/lib/ReactRouterHelpers.ts';
import { ParkingaboAsyncLoadedSection } from '../../components/layout/ParkingaboAsyncLoadedSection.tsx';
import { Box, Stack } from '@mui/material';
import { StatusBar } from '../../components/StatusBar.tsx';
import { DateTime } from 'luxon';
import { ParkingaboListItem } from '../../components/ParkingaboMenuListItem.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { ErrorOutlined, Receipt } from '@mui/icons-material';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { Navigate } from 'react-router-dom';

interface BadgePayStationPaymentMonthBucket {
    yearMonth: string;
    monthlyPayments: ParkingaboBadgePayment[];
}

const enum ParkingaboBadgePaymentType {
    PAY_STATION_PAYMENT = 'PAY_STATION_PAYMENT',
    E_COMMERCE_PAYMENT = 'E_COMMERCE_PAYMENT',
}

type ParkingaboBadgePayment =
    | ParkingaboBadgePayStationPayment
    | ParkingaboBadgeECommercePayment;

interface ParkingaboBadgePayStationPayment {
    type: ParkingaboBadgePaymentType.PAY_STATION_PAYMENT;
    payStationPaymentId: string;
    paidAt: string;
    paidAmountString: string;
    productName: Message;
}

interface ParkingaboBadgeECommercePayment {
    type: ParkingaboBadgePaymentType.E_COMMERCE_PAYMENT;
    contractExternalId: string;
    paidAt: string;
    paidAmountString: string;
    productName: Message;
}

export function BadgeNoAppIndividualPaymentsRoute() {
    const badgeLabelNr = useParamOrNull('badgeLabelNr');
    const [payStationPaymentsState] = useServerFetch<
        BadgePayStationPaymentMonthBucket[],
        { badgeLabelNr: string }
    >(
        ({ badgeLabelNr }) => ({
            url: `/ui-api/parkingabo/badge/${badgeLabelNr}/no-app-individual-payments`,
        }),
        badgeLabelNr ? { badgeLabelNr: badgeLabelNr } : null,
    );

    if (!badgeLabelNr) {
        return <Navigate to={'/'} />;
    }
    return (
        <ParkingaboAsyncLoadedSection
            state={payStationPaymentsState}
            render={payStationPaymentMonthlyBuckets => (
                <BadgePayStationPayments
                    payStationPaymentMonthlyBuckets={
                        payStationPaymentMonthlyBuckets
                    }
                    badgeLabelNr={badgeLabelNr}
                />
            )}
        />
    );
}

export function BadgePayStationPayments({
    payStationPaymentMonthlyBuckets,
    badgeLabelNr,
}: {
    payStationPaymentMonthlyBuckets: BadgePayStationPaymentMonthBucket[];
    badgeLabelNr: string;
}) {
    const hasNoReceipt = payStationPaymentMonthlyBuckets.length === 0;
    return (
        <ParkingaboLayoutWithHeader
            scrollable
            noGutter
            title={
                <Localized
                    de="Quittungen"
                    fr="Reçus"
                    it="Ricevute"
                    en="Receipts"
                />
            }
        >
            <Stack direction={'column'}>
                {hasNoReceipt ? <NoReceipt /> : <ReceiptInfo />}
                {payStationPaymentMonthlyBuckets.map(payStationPayment => (
                    <MonthlyPaystationPayments
                        key={payStationPayment.yearMonth}
                        payStationPaymentMonthlyBucket={payStationPayment}
                        badgeLabelNr={badgeLabelNr}
                    />
                ))}
            </Stack>
        </ParkingaboLayoutWithHeader>
    );
}

function MonthlyPaystationPayments({
    badgeLabelNr,
    payStationPaymentMonthlyBucket,
}: {
    payStationPaymentMonthlyBucket: BadgePayStationPaymentMonthBucket;
    badgeLabelNr: string;
}) {
    const { language } = useLanguage();
    return (
        <>
            <StatusBar>
                {Formatter.monthNameYear(
                    DateTime.fromISO(payStationPaymentMonthlyBucket.yearMonth),
                    Formatter.getLocaleFromString(language),
                )}
            </StatusBar>
            {payStationPaymentMonthlyBucket.monthlyPayments.map(payment => {
                switch (payment.type) {
                    case ParkingaboBadgePaymentType.PAY_STATION_PAYMENT:
                        return (
                            <ParkingaboListItem
                                variableHeight
                                key={payment.payStationPaymentId}
                                onClick={() =>
                                    openNewTab(
                                        `/ui-api/parkingabo/pay-station-payment/${payment.payStationPaymentId}/receipt`,
                                    )
                                }
                            >
                                <PaymentEntry payment={payment} />
                            </ParkingaboListItem>
                        );
                    case ParkingaboBadgePaymentType.E_COMMERCE_PAYMENT:
                        return (
                            <ParkingaboListItem
                                variableHeight
                                key={payment.contractExternalId}
                                onClick={() =>
                                    openNewTab(
                                        `/ui-api/parkingabo/badge/${badgeLabelNr}/badge-ecommerce-payment/${payment.contractExternalId}/receipt`,
                                    )
                                }
                            >
                                <PaymentEntry payment={payment} />
                            </ParkingaboListItem>
                        );
                }
            })}
        </>
    );
}

function PaymentEntry({ payment }: { payment: ParkingaboBadgePayment }) {
    return (
        <Stack
            direction="column"
            sx={{
                width: '100%',
            }}
        >
            <Stack
                direction="row"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 'bold',
                }}
            >
                <Box>
                    {Formatter.dayMonthYearHourMinute(
                        DateTime.fromISO(payment.paidAt),
                    )}
                </Box>
                <Box>{payment.paidAmountString}</Box>
            </Stack>
            <Box sx={{ fontWeight: 400 }}>
                <Localized {...payment.productName} />
            </Box>
        </Stack>
    );
}

function NoReceipt() {
    return (
        <Stack
            direction="column"
            sx={{
                alignItems: 'center',
                textAlign: 'center',
                padding: theme => theme.spacing(3),
            }}
            spacing={2}
        >
            <ErrorOutlined fontSize="large" />
            <Box>
                <Localized
                    de="Für diesen Badge besteht noch keine Quittung."
                    fr="Aucun reçu n’est encore disponible pour ce badge."
                    it="Non è ancora disponibile alcuna ricevuta per questo badge."
                    en="There is no receipt yet for this badge."
                />
            </Box>
        </Stack>
    );
}

function ReceiptInfo() {
    return (
        <Stack
            direction="row"
            spacing={1}
            sx={theme => ({
                alignItems: 'center',
                padding: theme.spacing(3),
                backgroundColor: theme.palette.grey[300],
            })}
        >
            <Receipt fontSize="large" />
            <Box sx={{ fontSize: '14px' }}>
                <Localized
                    de="Hier können Sie die Quittungen für die mit diesem Badge getätigten und an der Kasse bazahlten Parkvorgänge herunterladen."
                    fr="Vous pouvez télécharger ici les reçus des sessions de stationnement effectuées avec ce badge et payées à la caisse."
                    it="Qui è possibile scaricare le ricevute per le soste effettuate con questo badge e pagate alla cassa."
                    en="Here you can download the receipts for parking sessions carried out with this badge and paid for at the pay station."
                />
            </Box>
        </Stack>
    );
}
